import { HttpClient } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { LoaderFullService } from '@fragments/loader-full/loader-full.service'
import { BASE_URL, ListBaseService, SnackbarBaseService } from '@megaphone/bases'
import { MegaOrganisationService, MegaSessionService } from '@megaphone/services'
import { ApiResponse, Organisation, OrganisationStatistics } from '@megaphone/types'
import { BehaviorSubject, filter, takeUntil } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class OrganisationService extends ListBaseService {
  availableAccesses: string[] = [] // Sidenav Plugin to be shown
  onEditablePluginChanged$: BehaviorSubject<string[]> = new BehaviorSubject(this.availableAccesses)
  statistics$: BehaviorSubject<OrganisationStatistics> = new BehaviorSubject(null)
  organisation$: BehaviorSubject<boolean> = new BehaviorSubject(false)

  constructor(
    private _megaSessionService: MegaSessionService,
    private _megaOrganisationService: MegaOrganisationService,
    private _loaderFull: LoaderFullService,
    @Inject(BASE_URL) _baseUrl: string,
    _httpClient: HttpClient,
    _snackbarService: SnackbarBaseService,
  ) {
    super(
      {
        sorts: [
          {
            parameter: 'name',
            operator: 'asc',
          },
        ],
      },
      _baseUrl,
      '/organisations',
      _httpClient,
      _snackbarService,
    )

    this._megaOrganisationService.attributes$
      .pipe(
        filter((event) => (event?.id && event.id != 0 ? true : false)),
        takeUntil(this._unsubscribe$),
      )
      .subscribe((value) => {
        this.getAllItems()
        this.getStatistics(value.id)
        localStorage.setItem('active_organisation_id', value.id.toString())
      })

    window.addEventListener('storage', (event) => {
      if (
        event.key === 'active_organisation_id' &&
        event.newValue &&
        event.newValue !== event.oldValue
      ) {
        const pathSegments = window.location.pathname.split('/')
        if (pathSegments[1] && !isNaN(Number(pathSegments[1]))) {
          pathSegments[1] = event.newValue
          window.location.href =
            window.location.origin +
            pathSegments.join('/') +
            window.location.search +
            window.location.hash
        } else {
          window.location.reload()
        }
      }
    })


    this._megaOrganisationService.availableAccesses$
      .pipe(
        filter((event) => (event?.length > 0 ? true : false)),
        takeUntil(this._unsubscribe$),
      )
      .subscribe((availableAccesses: string[]) => {
        const newAccess = availableAccesses.map((pluginName) => {
          if (
            this._megaSessionService.session.roles.some((role) =>
              ['admin', 'super-admin'].includes(role.role),
            ) ||
            this._megaSessionService.session.roles.some((role) => role.role.includes(pluginName))
          )
            return pluginName
        })

        if (this._megaOrganisationService.availableChannels$.value?.website)
          newAccess.push('websites')

        this.onEditablePluginChanged$.next(newAccess)
      })
  }

  getStatistics(id: string): Promise<OrganisationStatistics> {
    return new Promise((resolve) => {
      this._httpClient.get(`${this.url}/${id}/statistics`).subscribe((response: ApiResponse) => {
        this.statistics$.next(response.attributes)
        resolve(response.attributes)
      })
    })
  }

  buySMS(smsAmount: number): Promise<void> {
    return new Promise((resolve) => {
      const data = { smsPaid: smsAmount }
      const organisationAttributes = this._megaOrganisationService.attributes$.getValue()

      if (organisationAttributes)
        this._httpClient.put(`${this.url}/${organisationAttributes.id}/sms`, data).subscribe({
          next: () => resolve(this._snackbarService.showSnackBar('success')),
          error: () => {
            this._snackbarService.showSnackBar('error', 'ERROR_UPDATE')
            resolve()
          },
        })
    })
  }

  setOrganisation(organisation: Organisation): Promise<void> {
    this._loaderFull.setLoading(true)

    return new Promise((resolve) => {
      this._httpClient.get(`${this.url}/${organisation.id}/impersonate`).subscribe({
        next: async () => {
          await this.init()
          await this._megaOrganisationService.init(true)
          await this._megaSessionService.initSession()
          this.organisation$.next(true)

          this._loaderFull.setLoading(false)

          resolve()
        },
        error: () => {
          this._snackbarService.showSnackBar('error', 'ERROR_UPDATE')
          this._loaderFull.setLoading(false)
          resolve()
        },
      })
    })
  }
}
